import { Box, FontColor, Icon, IconName, useBreakpoint } from '@palmetto/palmetto-components';
import groupBy from 'lodash/groupBy';
import { DateTime } from 'luxon';
import { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { AccountActivityRecord } from 'types';

import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import { InfiniteScroll } from '../InfiniteScroll';

import { getActivityHistoryComponent } from './getActivityHistoryComponent';

export const convertIsoToDateMed = (isoDate: string) =>
  isoDate ? DateTime.fromISO(isoDate).toLocaleString(DateTime.DATE_MED) : null;

export const convertIsoToTimeSimple = (isoDate: string) =>
  isoDate ? DateTime.fromISO(isoDate).toLocaleString(DateTime.TIME_SIMPLE) : null;

export const InfiniteActivityList = ({
  activities,
  fetchNextPage,
  isLoading,
  hasMore,
}: {
  activities: AccountActivityRecord[] | undefined;
  fetchNextPage: () => void;
  isLoading: boolean;
  hasMore: boolean;
}) => {
  const data = activities ?? [];
  const activitiesGroupedByDate = groupBy(data, (activity) => convertIsoToDateMed(activity.createdAt));
  const { claims } = useAuth();

  const components = Object.entries(activitiesGroupedByDate).map(([date, groupedActivities]) => {
    const activityComponents = getActivityHistoryComponent(groupedActivities, isPalmettoFinanceUser(claims));

    return (
      <Box margin="md 0 0 0" childGap="md" key={date}>
        <Box fontSize="md" fontWeight="bold" position="sticky" background="secondary" style={{ top: 0 }}>
          {date}
        </Box>
        {activityComponents.map(
          (activityComponent) =>
            activityComponent &&
            activityComponent.event && (
              <ActivityHistoryRow
                key={activityComponent.event.id}
                time={activityComponent.event.createdAt}
                icon={activityComponent.icon as IconName}
                iconColor={activityComponent.iconColor as FontColor}
              >
                <activityComponent.component event={activityComponent.event} />
              </ActivityHistoryRow>
            ),
        )}
      </Box>
    );
  });

  return (
    <Box display="block" padding={{ base: '0 lg', desktop: '0' }} fontSize="sm">
      {components.length > 0 ? (
        <InfiniteScroll fetchNextPage={fetchNextPage} isLoading={isLoading} hasMore={hasMore}>
          {components}
        </InfiniteScroll>
      ) : (
        'No activity history at this time.'
      )}
    </Box>
  );
};

interface ActivityHistoryRowProps {
  time: string;
  icon?: IconName;
  iconColor?: FontColor;
  children?: ReactNode;
}

export const ActivityHistoryRow: React.FC<ActivityHistoryRowProps> = ({
  time,
  icon,
  iconColor = 'body-tertiary',
  children,
}: ActivityHistoryRowProps) => {
  const { isPhone } = useBreakpoint();

  return (
    <Box
      direction="row"
      padding="sm 0"
      childGap="md"
      width="100"
      alignItems="flex-start"
      style={{ lineHeight: 'var(--size-line-height-text)' }}
    >
      <Box minWidth="lg" width="lg" title={time}>
        {convertIsoToTimeSimple(time)}
      </Box>
      {!isPhone && (
        <Box minWidth="sm" padding="2xs 0 0 0" width="sm" color={iconColor}>
          {icon && <Icon size="md" name={icon} />}
        </Box>
      )}
      <ErrorBoundary
        fallback={<Box style={{ fontStyle: 'italic' }}>(There was an error displaying this entry)</Box>}
      >
        <Box flex="auto">{children}</Box>
      </ErrorBoundary>
    </Box>
  );
};
